// ── CAPR S2 — the assessment workflow's words, declared ONCE ─────────────────────────────────
//
// ⚠ ONE HOME FOR EVERY SENTENCE THE ASSESSMENT SCREENS SAY ABOUT EVIDENCE AND DECISIONS. The Hub's
//   assessment screen, the approver view, the Tasks page and the history all describe the same facts;
//   four copies would drift, and the first drift would be a screen claiming more than S2 does.
//
// ⚠ CAPR S4 — TWO STATUSES, AND THE WORDS CLAIM NO MORE THAN THE CHECK. The server decides each item
//   at the approve boundary: "Evidence present & integrity-confirmed at approval" (available, the
//   latest conclusive check matches the capture, within the catalogue's review advice) or "Unverified
//   at approval" (anything else, unknown and inconclusive included). The confirmed status asserts
//   availability, integrity and recency ONLY — never that the evidence is adequate, sufficient or
//   validated for the control; a permanent copy guard holds every sentence here to that.
//   (S1 and S2 recorded every item unverified; their decisions still read that way.)

import {
  CTS_ADVERSE_CHANGE_LABEL, CTS_EVIDENCE_VERIFICATION_LABEL, CTS_IMPLEMENTATION_STATUS_LABEL,
  CTS_NOT_YET_ASSESSED_LABEL, CTS_NO_SUPPORTING_EVIDENCE_LABEL, CTS_UNVERIFIED_REASON_LABEL,
  type CtsAdverseChange, type CtsImplementationStatus, type CtsUnverifiedReason,
} from '@grc/shared';

/** The status label, or the DERIVED "Not Yet Assessed" when no approved decision exists. */
export function statusLabel(status: CtsImplementationStatus | null): string {
  return status ? CTS_IMPLEMENTATION_STATUS_LABEL[status] : CTS_NOT_YET_ASSESSED_LABEL;
}

/** "Reconfirm" is ONE choice: the prior status, chosen again (it still needs a rationale). */
export const reconfirmLabel = (prior: CtsImplementationStatus): string =>
  `Reconfirm ${CTS_IMPLEMENTATION_STATUS_LABEL[prior]}`;

export const UNVERIFIED_LABEL = CTS_EVIDENCE_VERIFICATION_LABEL.unverified_at_approval;
export const VERIFIED_LABEL = CTS_EVIDENCE_VERIFICATION_LABEL.verified_accepted;
export const VERIFIED_EXPLAINER =
  'Available at its source, the latest check matches what was captured, and it is within the catalogue’s '
  + 'recommended review cadence. This says nothing about whether the evidence is enough for the control.';
export const UNVERIFIED_EXPLAINER =
  'It can still be relied on. It is recorded with the assessor’s note as relied on unverified — never as '
  + 'confirmed evidence.';
/** ⓘ Shown on the LIVE surfaces: the verdict is how the item stands now, and approval checks again. */
export const VERIFICATION_NOW_NOTE =
  'How each item stands now. VeriGRC checks again at the moment of approval, and records what it finds.';
export const VERIFICATION_UNAVAILABLE = 'Its verification could not be read. Reload to see it.';
export const ACK_NOTE_LABEL = 'Why this evidence supports the assessment';
export const ACK_NOTE_HINT =
  'Required for each selected item — it is recorded with the decision. For an unverified item, it is your '
  + 'acknowledgment of the conditions shown above.';
export const AT_SUBMIT_LABEL = 'When the assessor submitted';
export const ADVERSE_CHANGE_WARNING =
  'Changed since the assessor submitted, so their note does not cover it — approving will be refused. '
  + 'Return the work so the assessor can acknowledge it:';

/** The server's reason, in words. */
export const reasonText = (r: CtsUnverifiedReason): string => CTS_UNVERIFIED_REASON_LABEL[r];
/** The server's adverse change, in words. */
export const adverseChangeText = (c: CtsAdverseChange): string => CTS_ADVERSE_CHANGE_LABEL[c];

export const NO_EVIDENCE_LABEL = CTS_NO_SUPPORTING_EVIDENCE_LABEL;
export const NO_EVIDENCE_EXPLAINER =
  'This assessment relies on no captured evidence. The rationale is the whole basis for the decision.';
export const NO_EVIDENCE_ACKNOWLEDGE = 'I am submitting this assessment with no supporting evidence';

export const PRIOR_BASIS_MARKER = 'Relied on in the last approval';
export const NO_PRIOR_ASSESSMENT = 'No prior assessment — this control has not been approved before.';

export const SUBMIT_ACTION = 'Submit for review';
export const SUBMIT_WARNING =
  'The approver will review this proposal exactly as submitted. Until they decide, it can only be changed '
  + 'if they return it to you.';
export const SUBMIT_ACKNOWLEDGE = 'I have checked the status, rationale and evidence I am submitting';

export const APPROVE_ACTION = 'Approve';
export const APPROVE_WARNING =
  'Approving records this proposal as the control’s implementation status, with you as the approver. '
  + 'A decision cannot be edited afterwards; a correction is a new assessment.';
export const APPROVE_ACKNOWLEDGE = 'I have reviewed this proposal and the evidence it relies on';

export const RETURN_LABEL = 'What needs to change';
export const START_ACTION = 'Start assessment';

/** Who decided a self-approved decision — said as a fact, never as a claim about separation of duties. */
export const SELF_APPROVED_NOTE = 'Approved by the person who assessed it';

// ── server errors, by CODE ───────────────────────────────────────────────────────────────────

export interface CtsEvidenceProblem {
  artifactId: string;
  name: string | null;
  problem: 'not_found' | 'withdrawn' | 'not_linked_to_control';
}
export interface CtsApiError { code: string | null; message: string | null; items: CtsEvidenceProblem[] }

/** The server's first error — its code, its message and any evidence items it named. */
export function ctsApiError(err: unknown): CtsApiError {
  const e = err as { response?: { data?: { errors?: { code?: string; message?: string; items?: CtsEvidenceProblem[] }[] } } };
  const first = e?.response?.data?.errors?.[0];
  return { code: first?.code ?? null, message: first?.message ?? null, items: Array.isArray(first?.items) ? first!.items! : [] };
}

/**
 * ⚠ TWO 409s, TWO DIFFERENT THINGS TO DO — told apart by CODE, never by status. A conflict means
 * someone moved the work first (reload); an invalid move means the move is no longer on offer from
 * where the work now is. A stale-evidence refusal names the items and says what to do next. Every
 * other refusal is the server's own sentence.
 */
export function workflowErrorText(err: unknown): string {
  const e = ctsApiError(err);
  switch (e.code) {
    case 'TRANSITION_CONFLICT':
      return 'This work changed while you were acting on it — someone else moved it first. Nothing was saved. Reload to see where it is now.';
    case 'TRANSITION_INVALID':
      return 'That move is no longer available from where this work is now. Nothing was saved. Reload to see the current state.';
    default:
      return e.message ?? 'That didn’t go through. The change was not saved.';
  }
}

export const PROBLEM_LABEL: Record<CtsEvidenceProblem['problem'], string> = {
  not_found: 'no longer exists',
  withdrawn: 'has been withdrawn',
  not_linked_to_control: 'is no longer linked to this control',
};
